import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from 'react';

import {
  copyInstanceDimensionUnits,
  dimensionUnitStorageKey,
  isDimensionField,
  parseDimensionUnits,
  pinInstanceDimensionUnits,
  pinListedInstanceDimensionUnits,
  sideForField,
  unitCodeForSide,
  type DimensionUnits,
} from './dimensionUnitChoices';
import { PreferencesService } from '../services/preferencesService';

import type { ChipSide } from '../utils/fieldUnitConversion';

type DimensionUnitContextValue = {
  units: DimensionUnits;
  setSide: (fieldKey: string, side: ChipSide, instanceId?: number) => void;
  copyInstance: (
    sourceId: number,
    destId: number,
    fields: ReadonlySet<string>,
  ) => void;
  pinInstance: (instanceId: number, fields: ReadonlySet<string>) => void;
  registerListed: (
    sectionId: string,
    instanceIds: readonly number[],
    fields: ReadonlySet<string>,
  ) => void;
};

const DimensionUnitContext = createContext<DimensionUnitContextValue | null>(
  null,
);

export function DimensionUnitsProvider({
  units,
  onChange,
  loadDefaults,
  children,
}: {
  units: unknown;
  onChange: (next: DimensionUnits) => void;
  loadDefaults: boolean;
  children: React.ReactNode;
}): React.ReactElement {
  const parsed = parseDimensionUnits(units);
  const seeded = useRef(false);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const parsedRef = useRef(parsed);
  parsedRef.current = parsed;
  const listedRef = useRef(
    new Map<string, { ids: readonly number[]; fields: ReadonlySet<string> }>(),
  );

  useEffect(() => {
    if (!loadDefaults || seeded.current) return;
    if (Object.keys(parsedRef.current).length > 0) {
      seeded.current = true;
      return;
    }
    let cancelled = false;
    PreferencesService.getDimensionUnitDefaults()
      .then(defaults => {
        if (cancelled || seeded.current) return;
        if (Object.keys(parsedRef.current).length > 0) {
          seeded.current = true;
          return;
        }
        seeded.current = true;
        if (Object.keys(defaults).length > 0) onChangeRef.current(defaults);
      })
      .catch(() => {
        seeded.current = true;
      });
    return () => {
      cancelled = true;
    };
  }, [loadDefaults]);

  const registerListed = (
    sectionId: string,
    instanceIds: readonly number[],
    fields: ReadonlySet<string>,
  ) => {
    listedRef.current.set(sectionId, { ids: instanceIds, fields });
  };

  const setSide = (fieldKey: string, side: ChipSide, instanceId?: number) => {
    seeded.current = true;
    const code = unitCodeForSide(fieldKey, side);
    if (!code) return;
    // Use the latest units (not a stale render closure). Instance chips also
    // write the field key so a seeded Other preference cannot outlive America.
    // Pin every listed child first: the first row often has only the field key,
    // so a duplicate's edit would otherwise change that row too.
    let base = parsedRef.current;
    const fieldOnly = new Set([fieldKey]);
    for (const entry of listedRef.current.values()) {
      if (!entry.fields.has(fieldKey)) continue;
      base = pinListedInstanceDimensionUnits(base, entry.ids, fieldOnly);
    }
    const next: DimensionUnits = { ...base };
    if (instanceId != null) {
      next[dimensionUnitStorageKey(fieldKey, instanceId)] = code;
    }
    next[fieldKey] = code;
    onChange(next);
  };

  const copyInstance = (
    sourceId: number,
    destId: number,
    fields: ReadonlySet<string>,
  ) => {
    seeded.current = true;
    const pinned = pinInstanceDimensionUnits(
      parsedRef.current,
      sourceId,
      fields,
    );
    onChange(copyInstanceDimensionUnits(pinned, sourceId, destId, fields));
  };

  const pinInstance = (instanceId: number, fields: ReadonlySet<string>) => {
    const next = pinInstanceDimensionUnits(
      parsedRef.current,
      instanceId,
      fields,
    );
    if (next === parsedRef.current) return;
    seeded.current = true;
    onChange(next);
  };

  return (
    <DimensionUnitContext.Provider
      value={{
        units: parsed,
        setSide,
        copyInstance,
        pinInstance,
        registerListed,
      }}
    >
      {children}
    </DimensionUnitContext.Provider>
  );
}

export function useDimensionUnits(): DimensionUnits {
  return useContext(DimensionUnitContext)?.units ?? {};
}

export function useCopyInstanceDimensionUnits(): (
  sourceId: number,
  destId: number,
  fields: ReadonlySet<string>,
) => void {
  const context = useContext(DimensionUnitContext);
  return (sourceId, destId, fields) => {
    context?.copyInstance(sourceId, destId, fields);
  };
}

export function usePinInstanceDimensionUnits(): (
  instanceId: number,
  fields: ReadonlySet<string>,
) => void {
  const context = useContext(DimensionUnitContext);
  return (instanceId, fields) => {
    context?.pinInstance(instanceId, fields);
  };
}

export function useRegisterListedInstanceDimensionUnits(): (
  sectionId: string,
  instanceIds: readonly number[],
  fields: ReadonlySet<string>,
) => void {
  const context = useContext(DimensionUnitContext);
  const registerRef = useRef(context?.registerListed);
  registerRef.current = context?.registerListed;
  return useCallback((sectionId, instanceIds, fields) => {
    registerRef.current?.(sectionId, instanceIds, fields);
  }, []);
}

export function useDimensionUnitController(
  fieldKey: string | undefined,
  instanceId?: number,
): { side: ChipSide; setSide: (side: ChipSide) => void } | null {
  const context = useContext(DimensionUnitContext);
  if (!context || !fieldKey || !isDimensionField(fieldKey)) return null;
  const side = sideForField(context.units, fieldKey, instanceId);
  return {
    side,
    setSide: next => context.setSide(fieldKey, next, instanceId),
  };
}
